import { useEffect, useMemo, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import * as Y from 'yjs';
import { Awareness, applyAwarenessUpdate, encodeAwarenessUpdate, removeAwarenessStates } from 'y-protocols/awareness';
import { useAuthStore } from '../store/authStore';

export interface NoteCollaborator {
  socketId: string;
  userId: string;
  name: string;
  photoUrl: string | null;
  color: string;
  cursor?: { blockId: string; start: number; end: number } | null;
}

const REMOTE_ORIGIN = Symbol('notes-remote');
const colors = ['#d54b9f', '#5b9fed', '#d69e3d', '#68b89a', '#a77ee8', '#e27868'];

export function useNoteCollaboration(noteId: string | null, enabled: boolean) {
  const currentUser = useAuthStore(state => state.user);
  const [doc, setDoc] = useState<Y.Doc | null>(null);
  const [awareness, setAwareness] = useState<Awareness | null>(null);
  const [collaborators, setCollaborators] = useState<NoteCollaborator[]>([]);
  const [connected, setConnected] = useState(false);
  const [synced, setSynced] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [accessRevoked, setAccessRevoked] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'connecting' | 'saving' | 'saved'>('connecting');
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!noteId || !enabled) {
      setDoc(null);
      setAwareness(null);
      setCollaborators([]);
      setConnected(false);
      setSynced(false);
      setAccessRevoked(false);
      setSaveStatus('connecting');
      return;
    }

    const nextDoc = new Y.Doc();
    const nextAwareness = new Awareness(nextDoc);
    const color = colors[Math.abs((currentUser?.id || '').split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)) % colors.length];
    nextAwareness.setLocalStateField('user', {
      id: currentUser?.id,
      name: currentUser?.name || 'Collaborator',
      color,
      photoUrl: currentUser?.photoUrl || null,
    });
    setDoc(nextDoc);
    setAwareness(nextAwareness);

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    const socket = io(`${apiUrl}/notes`, {
      auth: { token: localStorage.getItem('token') },
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionDelay: 750,
      reconnectionAttempts: Infinity,
    });
    socketRef.current = socket;

    const sendDocumentUpdate = (update: Uint8Array, origin: any) => {
      if (origin === REMOTE_ORIGIN || !socket.connected) return;
      setSaveStatus('saving');
      socket.emit('notes:update', { noteId, update: Array.from(update) });
    };
    const sendAwarenessUpdate = ({ added, updated, removed }: { added: number[]; updated: number[]; removed: number[] }, origin: any) => {
      if (origin === REMOTE_ORIGIN || !socket.connected) return;
      const clients = added.concat(updated, removed);
      socket.emit('notes:awareness', { noteId, update: Array.from(encodeAwarenessUpdate(nextAwareness, clients)) });
    };
    nextDoc.on('update', sendDocumentUpdate);
    nextAwareness.on('update', sendAwarenessUpdate);

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('notes:join', { noteId }, (response: { ok: boolean; canEdit?: boolean }) => {
        setCanEdit(Boolean(response?.ok && response.canEdit));
        if (response?.ok) {
          socket.emit('notes:awareness', {
            noteId,
            update: Array.from(encodeAwarenessUpdate(nextAwareness, [nextDoc.clientID])),
          });
        }
      });
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('notes:sync', ({ update }: { update: number[] }) => {
      Y.applyUpdate(nextDoc, Uint8Array.from(update), REMOTE_ORIGIN);
      setSynced(true);
      setSaveStatus('saved');
      socket.emit('notes:update', { noteId, update: Array.from(Y.encodeStateAsUpdate(nextDoc)) });
    });
    socket.on('notes:update', ({ update }: { update: number[] }) => {
      Y.applyUpdate(nextDoc, Uint8Array.from(update), REMOTE_ORIGIN);
    });
    socket.on('notes:awareness', ({ update }: { update: number[] }) => {
      applyAwarenessUpdate(nextAwareness, Uint8Array.from(update), REMOTE_ORIGIN);
    });
    socket.on('notes:presence', (next: NoteCollaborator[]) => {
      setCollaborators(next);
      const activeUserIds = new Set(next.map(person => person.userId));
      const staleClients: number[] = [];
      nextAwareness.getStates().forEach((state: any, clientId) => {
        if (clientId !== nextDoc.clientID && state.user?.id && !activeUserIds.has(state.user.id)) staleClients.push(clientId);
      });
      if (staleClients.length) removeAwarenessStates(nextAwareness, staleClients, REMOTE_ORIGIN);
      if (socket.connected) {
        socket.emit('notes:awareness', {
          noteId,
          update: Array.from(encodeAwarenessUpdate(nextAwareness, [nextDoc.clientID])),
        });
      }
    });
    socket.on('notes:saved', () => setSaveStatus('saved'));
    socket.on('notes:access_revoked', () => {
      setAccessRevoked(true);
      setCanEdit(false);
      setCollaborators([]);
      socket.disconnect();
    });
    socket.on('connect_error', () => setConnected(false));

    return () => {
      socket.emit('notes:leave');
      socket.disconnect();
      nextDoc.off('update', sendDocumentUpdate);
      nextAwareness.off('update', sendAwarenessUpdate);
      removeAwarenessStates(nextAwareness, Array.from(nextAwareness.getStates().keys()), 'unmount');
      nextAwareness.destroy();
      nextDoc.destroy();
      socketRef.current = null;
      setDoc(null);
      setAwareness(null);
      setSynced(false);
      setCollaborators([]);
    };
  }, [currentUser?.id, currentUser?.name, currentUser?.photoUrl, enabled, noteId]);

  const provider = useMemo(() => awareness ? { awareness } : null, [awareness]);
  return { doc, provider, collaborators, connected, synced, canEdit, accessRevoked, saveStatus };
}
